import React, { useState, useEffect, useRef } from "react";
import Button from "../Button/Button";
import ButtonGroup from '../Button/ButtonGroup/ButtonGroup';
import cbModal, { ModalHeader, ModalFooter, ModalBody } from '../Modal/Modal';
import DragAndUploadFile from "./helper/DragAndUploadFile";
import Icon from "../Icon2/Icon";
import Checkbox from "../Checkbox/Checkbox";
import Tooltip from "../Tooltip/Tooltip";
import ProgressBar from "../ProgressBar/ProgressBar";
import axios from "axios";
import './DragAndDropImport.css'

export type dragAndDropImportProps = {
  handleUpload: Function,
  setShouldRefreshTable: Function,
  showAtTopRef: any,
  label: string,
  supportedFileTypes: Array<string>,
  requireCheckbox: boolean,
  headerSubtext?: string,
  checkboxText?: string,
  instructionText?: string,
  fileTypeErrorText?: string,
  buttonLabel?: string,
  isButton?: boolean,
  fileSizeInstructionText?: string
}

export type dragAndDropImportModalProps = {
  handleUpload: Function,
  setShouldRefreshTable: Function,
  showAtTopRef: any,
  label: string,
  supportedFileTypes: Array<string>,
  requireCheckbox: boolean,
  headerSubtext?: string,
  checkboxText?: string,
  instructionText?: string,
  fileTypeErrorText?: string,
  closeModal?: Function,
  fileSizeInstructionText?: string
}

export const DragAndDropImportModal = (props: dragAndDropImportModalProps) => {
  // file to be uploaded
  const [file, setFile] = useState(null);

  // progress of the upload
  const [progress, setProgress] = useState(0);

  // uploading state
  const [uploading, setUploading] = useState(false);

  // generic error message state
  const [importFailedMessage, setImportFailedMessage] = useState('There was an error uploading the file. Please try again.');

  // error state
  const [errored, setErrored] = useState({
    isError: false,
    message: 'any'
  });

  // file uploaded state
  const [fileUploaded, setFileUploaded] = useState(false);

  // file type icon state
  const [fileTypeIcon, setFileTypeIcon] = useState('File');

  // show at top state in the table, only applicable when requireCheckbox is true
  const [newShowAtTop, setNewShowAtTop] = useState(false);

  // cancel token source of api call
  const cancelSourceRef = useRef(axios.CancelToken.source());

  // file uploaded ref
  const fileUploadedRef = useRef(false);

  const { setShouldRefreshTable, showAtTopRef, handleUpload, label, headerSubtext, checkboxText, instructionText, fileTypeErrorText, supportedFileTypes, requireCheckbox, fileSizeInstructionText } = props;

  const bytesToMB = (bytes, decimalPoint) => {

    if (bytes == 0) return '0 Bytes';
    var k = 1000,
      dm = decimalPoint || 2,
      sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  const handleDrop = (files: any) => {
    if (files.length > 1) {
      setErrored({
        isError: true,
        message: 'multipleFiles'
      });
      throw new Error('Multiple files are not supported');
    }

    const file = files[0];
    if (file) {
      const fileType = file.type;

      if (Array.isArray(supportedFileTypes) && supportedFileTypes.includes(fileType)) {
        console.log('Valid file:', file, typeof file);
        if (fileType === 'application/json') {
          setFileTypeIcon('JSON');
        }
        else if (fileType === 'text/csv') {
          setFileTypeIcon('CSV');
        }
        setFile(file);
      } else {
        setErrored({
          isError: true,
          message: 'fileType'
        });
        throw new Error('Invalid file type. Please drop a JSON or CSV file.');
      }
    }
  }

  useEffect(() => {
    console.log("File changed -- Import Taxonomy", file);
    if (file) {
      handleUpload(file, setShouldRefreshTable, setProgress, setFileUploaded, setErrored, cancelSourceRef, setUploading, fileUploadedRef, setImportFailedMessage);
    }
  }, [file])

  useEffect(() => {
    return () => {
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel('Upload canceled by cleanup');
      }
      if (fileUploadedRef.current) {
        setShouldRefreshTable(true);
      }
    };
  }, [])

  const handleCancel = () => {
    // Abort the ongoing upload request when the cancel button is clicked
    console.log("cancelSource", cancelSourceRef.current);

    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel('cancelByUser');
    }

    setUploading(false);
    setProgress(0);
    setErrored({
      isError: false,
      message: 'any'
    });
  };

  return (
    <div className="ReactModal__taxonomy__import" style={{ height: "600px" }}>
      <ModalHeader title={`Import ${label}`} closeModal={props.closeModal} />
      <ModalBody>
        <div className='ReactModal__taxonomy__import__header-subtext'>
          {headerSubtext ? headerSubtext : "Upload a .csv or .json file for taxonomy import. Refer to documentation for further assistance."}
        </div>
        {!uploading && !fileUploaded ? (
          <>
            <div className="ReactModal__taxonomy__import__drag-drop-wrapper">
              <DragAndUploadFile file={file} setFile={setFile} setProgress={setProgress} setUploading={setUploading} handleDrop={handleDrop} errored={errored} importFailedMessage={importFailedMessage} fileTypeErrorText={fileTypeErrorText} label={label} />
            </div>
            <div className="flex-justify">
              <div className="ReactModal__taxonomy__import__instruction-text">
                {instructionText ? instructionText : "File Supported: .csv and .json"}
              </div>
              {fileSizeInstructionText
                ? <div className="ReactModal__taxonomy__import__instruction-text">
                  {fileSizeInstructionText}
                </div>
                : null}
            </div>
          </>
        ) : (
          fileUploaded ?
            (
              <>
                <div className='ReactModal__taxonomy__import__uploading-wrapper'>
                  <div className='ReactModal__taxonomy__import__uploading-wrapper__details'>
                    <Icon version="v2" icon={fileTypeIcon}></Icon>
                    <div className="info-section">
                      <div className="import-file-details">
                        <div className="import-file-name">
                          {file.name}
                        </div>
                        <div className="import-file-size">
                          {`${bytesToMB(file?.size, 2)}`}
                        </div>
                      </div>
                      <div className="import-file-percentage-wrapper">
                        <div className="import-file-percentage imported">
                          {`Imported`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {requireCheckbox ? (
                  <div className="Checkbox-wrapper taxonomy-imported-checkbox">
                    <Checkbox
                      onClick={() => {
                      }}
                      onChange={() => {
                        showAtTopRef.current = !newShowAtTop;
                        setNewShowAtTop(!newShowAtTop);
                      }}
                      isLabelFullWidth={true}
                      label={checkboxText ? checkboxText : "Show this taxonomy at the top of the taxonomy list"}
                      checked={newShowAtTop}
                    />
                  </div>
                ) : null}
              </>
            ) :
            (
              <>
                <div className='ReactModal__taxonomy__import__uploading-wrapper'>
                  <div className='ReactModal__taxonomy__import__uploading-wrapper__details'>
                    <Icon version="v2" icon='File'></Icon>
                    <div className="info-section">
                      <div className="import-file-details">
                        <div className="import-file-name">
                          {file.name}
                        </div>
                        <div className="import-file-size">
                          {`${bytesToMB(file?.size, 2)}`}
                        </div>
                      </div>
                      <div className="import-file-percentage-wrapper">
                        <div className="import-file-percentage">
                          {`Importing...${progress}%`}
                        </div>
                        <Tooltip position="top" content="Cancel Import" appendTo={document.body}>
                          <div className="cancel-import-taxonomy" onClick={() => {
                            handleCancel();
                            console.log("cancel clicked");
                          }}>
                            <Icon version="v2" icon='CloseNoborder'></Icon>
                          </div>
                        </Tooltip>

                      </div>
                    </div>
                  </div>
                  <div className='ReactModal__taxonomy__import__uploading-wrapper__progress-bar'>
                    <ProgressBar percentage={progress} color={"#6C5CE7"} borderRadius={8} height={8} />
                  </div>
                </div>
              </>
            )
        )}
      </ModalBody>
      <ModalFooter>
        <Button version='v2' buttonType={fileUploaded ? "primary" : "tertiary"} onClick={props.closeModal}>
          {fileUploaded ? "Done" : "Cancel"}
        </Button>
      </ModalFooter>
    </div >
  );
}

const DragAndDropImport = (props: dragAndDropImportProps) => {
  const { buttonLabel, isButton } = props;
  const handleClick = () => {
    cbModal({
      component: (modalProps: any) => <DragAndDropImportModal {...modalProps} type="import" {...props} />,
      modalProps: {
        size: 'max',
        shouldCloseOnEsc: true,
        customClass: 'ReactModal__drag_drop_import-wrapper',
      },
      testId: 'taxonomy-import-modal'
    })
  }

  return isButton ? (
    <Button
      onClick={handleClick}
      buttonType="secondary"
      icon="Import"
    >
      {buttonLabel ? buttonLabel : "Import"}
    </Button>
  ) : <DragAndDropImportModal {...props} />;
}

export default DragAndDropImport;
